import React, { useState, useEffect } from 'react';
import styles from './CustomersManagement.module.css';
import { getCustomers, addCustomer, editCustomer, deleteCustomer } from '../services/apiCustomers.ts';
import Spinner from './Spinner.tsx';
import Modal from './Modal.tsx';
import ThirdPartyChemModal from './ThirdPartyChemModal.tsx';
import { toast } from 'react-toastify';

interface Customer {
  id: number;
  name: string;
  operator_number: string;
  water_source: string;
}

interface ModalData {
  isOpen: boolean;
  type: 'add' | 'edit' | 'delete' | 'thirdPartyChem';
  customer?: Customer;
}

const CustomersManagement: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalData>({ isOpen: false, type: 'add' });
  const [thirdPartyChemModal, setThirdPartyChemModal] = useState<{ isOpen: boolean; customer?: Customer }>({ 
    isOpen: false 
  });
  const [formData, setFormData] = useState<Omit<Customer, 'id'>>({
    name: '',
    operator_number: '',
    water_source: '',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<keyof Customer>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await getCustomers();
      setCustomers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const openAddModal = () => {
    setFormData({
      name: '',
      operator_number: '',
      water_source: '',
    });
    setModal({ isOpen: true, type: 'add' });
  };

  const openEditModal = (customer: Customer) => {
    setFormData({
      name: customer.name,
      operator_number: customer.operator_number,
      water_source: customer.water_source || '',
    });
    setModal({ isOpen: true, type: 'edit', customer });
  };

  const openDeleteModal = (customer: Customer) => {
    setModal({ isOpen: true, type: 'delete', customer });
  };
  
  const openThirdPartyChemModal = (customer: Customer) => {
    setThirdPartyChemModal({ isOpen: true, customer });
  };
  
  const closeThirdPartyChemModal = () => {
    setThirdPartyChemModal({ isOpen: false });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (modal.type === 'add') {
        await addCustomer(
          formData.name,
          formData.operator_number,
          formData.water_source
        );
        toast.success('Customer added successfully');
      } else if (modal.type === 'edit' && modal.customer) {
        await editCustomer(
          modal.customer.id,
          formData.name,
          formData.operator_number,
          formData.water_source
        );
        toast.success('Customer updated successfully');
      }
      
      closeModal();
      fetchCustomers();
    } catch (err) {
      console.error('Error saving customer:', err);
      toast.error('Failed to save customer. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!modal.customer) return;
    
    try {
      await deleteCustomer(modal.customer.id);
      toast.success('Customer deleted successfully');
      closeModal();
      fetchCustomers();
    } catch (err) {
      console.error('Error deleting customer:', err);
      toast.error('Failed to delete customer. Please try again.');
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle sort change
  const handleSortChange = (field: keyof Customer) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (customer.name && customer.name.toLowerCase().includes(searchLower)) ||
      (customer.operator_number && customer.operator_number.toLowerCase().includes(searchLower)) ||
      (customer.water_source && customer.water_source.toLowerCase().includes(searchLower))
    );
  });

  // Sort customers based on sort field and direction
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // String comparison
    aValue = String(aValue || '').toLowerCase();
    bValue = String(bValue || '').toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get sort indicator
  const getSortIndicator = (field: keyof Customer) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Customers Management</h2>
        <button className={styles.addButton} onClick={openAddModal}>Add New Customer</button>
      </div>
      
      {loading ? (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : (
        <>
          <div className={styles.controls}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
            </div>
          </div>
          
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => handleSortChange('name')} className={styles.sortableHeader}>
                    Company Name {getSortIndicator('name')}
                  </th>
                  <th onClick={() => handleSortChange('operator_number')} className={styles.sortableHeader}>
                    Operator Number {getSortIndicator('operator_number')}
                  </th>
                  <th onClick={() => handleSortChange('water_source')} className={styles.sortableHeader}>
                    Water Source {getSortIndicator('water_source')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={styles.noCustomers}>
                      {searchTerm ? 'No customers found matching your search.' : 'No customers found. Add some above.'}
                    </td>
                  </tr>
                ) : (
                  sortedCustomers.map(customer => (
                    <tr key={customer.id}>
                      <td>{customer.name}</td>
                      <td>{customer.operator_number}</td>
                      <td>{customer.water_source}</td>
                      <td>
                        <div className={styles.actions}>
                          <button 
                            className={styles.editButton} 
                            onClick={() => openEditModal(customer)}
                          >
                            Edit
                          </button>
                          <button 
                            className={styles.deleteButton} 
                            onClick={() => openDeleteModal(customer)}
                          >
                            Delete
                          </button>
                          <button 
                            className={styles.thirdPartyChemButton} 
                            onClick={() => openThirdPartyChemModal(customer)}
                          >
                            Manage Third Party Chems
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className={styles.pagination}>
            <p>Showing {sortedCustomers.length} of {customers.length} customers</p>
          </div>
        </>
      )}

      {/* Add/Edit Customer Modal */}
      {modal.isOpen && (modal.type === 'add' || modal.type === 'edit') && (
        <Modal
          isOpen={modal.isOpen}
          title={modal.type === 'add' ? 'Add New Customer' : 'Edit Customer'}
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Company Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="operator_number">Operator Number</label>
              <input
                type="text"
                id="operator_number"
                name="operator_number"
                value={formData.operator_number}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="water_source">Water Source</label>
              <input
                type="text"
                id="water_source"
                name="water_source"
                value={formData.water_source}
                onChange={handleInputChange}
              />
            </div>
            
            <div className={styles.formActions}>
              <button type="button" onClick={closeModal} className={styles.cancelButton}>
                Cancel
              </button>
              <button type="submit" className={styles.submitButton}>
                {modal.type === 'add' ? 'Add Customer' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {modal.isOpen && modal.type === 'delete' && modal.customer && (
        <Modal
          isOpen={modal.isOpen}
          title="Delete Customer"
          onClose={closeModal}
        >
          <div className={styles.deleteConfirmation}>
            <p>
              Are you sure you want to delete the customer "{modal.customer.name}"?
              This action cannot be undone.
            </p>
            
            <div className={styles.formActions}>
              <button type="button" onClick={closeModal} className={styles.cancelButton}>
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleDelete} 
                className={styles.deleteConfirmButton}
              >
                Delete Customer
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Third Party Chemicals Modal */}
      {thirdPartyChemModal.isOpen && thirdPartyChemModal.customer && (
        <ThirdPartyChemModal
          isOpen={thirdPartyChemModal.isOpen}
          onClose={closeThirdPartyChemModal}
          customerId={thirdPartyChemModal.customer.id}
          customerName={thirdPartyChemModal.customer.name}
        />
      )}
    </div>
  );
};

export default CustomersManagement;
